/**
 * Matchmaking. Online (M2): the real search, its time, and when a bot will fill in (20 s);
 * "Opponent found." or "Bot joined.", then VS. Offline (no server, or unreachable): a short fake
 * search (1–2.5 s) before a local bot, as in M1.
 * Landscape phones: designs/Caged VS Landscape (1a). Portrait and desktop: the matchmaking
 * screen of designs/Caged Home.
 */
import { FIGHT, levelOf, lookOf, ONLINE } from '@caged/shared';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '../../state/store';
import { ArenaBackdrop } from '../components/Backdrop';
import { FighterArt } from '../components/FighterArt';
import ui from '../components/ui.module.css';
import { clock2 } from '../format';
import { useKey, useLayoutMode, useMyFighter, useShortLandscape } from '../hooks';
import { LeagueLobby } from './LeagueLobby';
import '../ui.css';
import s from './MatchmakingScreen.module.css';

/** Fake search length (ms) and how long "Bot joined." shows before VS. */
const SEARCH_MIN = 1000;
const SEARCH_MAX = 2500;
const FOUND_HOLD = 1500;
const SUBS = ['looking', 'region', 'skill'] as const;

function QuickMatchmaking() {
  const { t } = useTranslation();
  const mode = useLayoutMode();
  const short = useShortLandscape();
  const match = useApp((st) => st.match);
  const mm = useApp((st) => st.matchmaking);
  const settings = useApp((st) => st.settings);
  const myFighter = useMyFighter();
  const progressState = useApp((st) => st.progress);
  const go = useApp((st) => st.go);
  const cancelMatchmaking = useApp((st) => st.cancelMatchmaking);
  const difficulty = settings.difficulty;
  const [searchMs] = useState(() => SEARCH_MIN + Math.random() * (SEARCH_MAX - SEARCH_MIN));
  const [elapsed, setElapsed] = useState(0);
  const offline = mm?.status === 'offline';
  const found = !!match && (offline ? elapsed >= searchMs : mm?.status === 'found');
  const vsBot = offline || !!mm?.vsBot;

  useEffect(() => {
    if (!mm) go('home');
  }, [mm, go]);

  useEffect(() => {
    if (!mm) return;
    const tick = () => setElapsed(Date.now() - mm.since);
    tick();
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, [mm]);

  useEffect(() => {
    if (!found) return;
    const id = setTimeout(() => go('vs'), FOUND_HOLD);
    return () => clearTimeout(id);
  }, [found, go]);

  const cancel = useCallback(() => cancelMatchmaking(), [cancelMatchmaking]);
  useKey('Escape', cancel);

  if (!mm) return null;

  // While searching online there is no match yet: the player's own card comes from their profile.
  const me = match?.player ?? { fighterId: myFighter, name: settings.playerName || 'You', level: levelOf(progressState).level, look: lookOf(progressState) };
  const botWait = ONLINE.matchmaking.botAfterSeconds;
  const botIn = Math.max(0, Math.ceil(botWait - elapsed / 1000));
  const sub = found ? t('screens.matchmaking.getReady') : t(`screens.matchmaking.sub.${SUBS[Math.floor(elapsed / 900) % SUBS.length]}`);
  const progress = found ? 1 : Math.min(1, offline ? elapsed / searchMs : elapsed / 1000 / botWait);
  const format = t('screens.ui.format', { rounds: FIGHT.rounds, seconds: FIGHT.roundSeconds });
  const botTag = offline ? t('screens.matchmaking.botTag', { difficulty: t(`screens.difficulty.${difficulty}`) }) : t('screens.matchmaking.onlineTag');
  const note = offline ? t('screens.matchmaking.botNote') : botIn > 0 ? t('screens.matchmaking.botIn', { seconds: botIn }) : t('screens.matchmaking.botSoon');

  // Art boxes have one height, so taller fighters don't make one card taller than the other.
  const artBox = (imgH: number) => ({ height: Math.round(imgH * 1.2) });

  const meCard = (imgH: number) => (
    <div className={s.card}>
      <div className={s.cardArt} style={artBox(imgH)}>
        <FighterArt id={me.fighterId} height={imgH} shadow="soft" alt={me.name} look={me.look} />
      </div>
      <div className={s.cardName}>{me.name}</div>
      <div className={s.cardTag}>{t('screens.matchmaking.youTag', { level: me.level ?? 1 })}</div>
    </div>
  );

  const oppCard = (imgH: number) => (
    <div className={[s.card, found ? s.cardFound : ''].join(' ')}>
      <div className={s.cardArt} style={artBox(imgH)}>
        {found && match ? (
          <FighterArt id={match.opponent.fighterId} height={imgH} flip shadow="soft" alt={match.opponent.name} look={match.opponent.look} />
        ) : (
          <div className={s.unknown} style={{ width: imgH, height: imgH }} aria-hidden="true">
            ?
          </div>
        )}
      </div>
      <div className={s.cardName}>{found && match ? match.opponent.name : '???'}</div>
      <div className={s.cardTag}>
        {found && match
          ? t(vsBot ? 'screens.matchmaking.botLevel' : 'screens.matchmaking.oppLevel', { level: match.opponent.level ?? 1 })
          : t('screens.matchmaking.searching')}
      </div>
    </div>
  );

  const title = (
    <>
      <h1 className={s.title} aria-live="polite">
        {found ? t(vsBot ? 'screens.matchmaking.found' : 'screens.matchmaking.foundHuman') : t('screens.matchmaking.finding')}
      </h1>
      <div className={[s.sub, found ? s.subFound : ''].join(' ')}>{sub}</div>
    </>
  );

  const bar = (
    <div className={s.bar} role="progressbar" aria-label={t('screens.matchmaking.searching')} aria-valuenow={Math.round(progress * 100)} aria-valuemin={0} aria-valuemax={100}>
      <div className={s.barFill} style={{ width: `${progress * 100}%` }} />
    </div>
  );

  const status = found ? t('screens.matchmaking.matched') : t('screens.matchmaking.searching');

  if (mode === 'landscape') {
    return (
      <div className={['cg-ui', s.root].join(' ')}>
        <ArenaBackdrop tone="menu" shade="linear-gradient(180deg,rgba(20,19,18,.9),rgba(20,19,18,.4) 45%,rgba(20,19,18,.95))" />
        <div className={s.land}>
          <div className={s.topRow}>
            <span>{t('screens.matchmaking.quickFight', { format })}</span>
            <span>{botTag}</span>
          </div>
          <div className={s.landMid}>
            {meCard(short ? 60 : 96)}
            <div className={s.landCenter}>
              {title}
              <div className={s.clock}>{clock2(elapsed / 1000)}</div>
            </div>
            {oppCard(short ? 60 : 96)}
          </div>
          <div className={s.landBottom}>
            <div className={s.landStatus}>
              <div className={s.statusRow}>
                <span className={ui.label}>{status}</span>
                {!found && <span className={s.note}>{note}</span>}
              </div>
              {bar}
            </div>
            <button type="button" className={ui.ghost} onClick={cancel}>
              {t('common.cancel')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={['cg-ui', s.root].join(' ')}>
      <ArenaBackdrop tone="menu" shade="linear-gradient(180deg,rgba(20,19,18,.9),rgba(20,19,18,.4) 45%,rgba(20,19,18,.95))" />
      <div className={s.port}>
        <div className={s.topRow}>
          <span>{t('screens.matchmaking.quickFightShort')}</span>
          <span>{botTag}</span>
        </div>
        <div className={s.portMid}>
          <div className={s.portTitle}>{title}</div>
          <div className={s.portCards}>
            {meCard(84)}
            <div className={s.vs} aria-hidden="true">
              VS
            </div>
            {oppCard(84)}
          </div>
        </div>
        <div className={s.portBottom}>
          <div className={s.statusRow}>
            <span className={ui.label}>{status}</span>
            <span className={ui.label}>{clock2(elapsed / 1000)}</span>
          </div>
          {bar}
          {found ? (
            <button type="button" className={ui.primary} onClick={() => go('vs')}>
              {t('screens.matchmaking.fight')}
              <span className={ui.arrow} aria-hidden="true">
                ▷
              </span>
            </button>
          ) : (
            <div className={s.noteCenter}>{note}</div>
          )}
          <button type="button" className={ui.ghost} onClick={cancel}>
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Matchmaking for a quick fight, or the lobby of today's league fight (M5). */
export function MatchmakingScreen() {
  const league = useApp((st) => !!st.matchmaking?.league);
  return league ? <LeagueLobby /> : <QuickMatchmaking />;
}
